import {
    Users, Clock, CheckCircle, TrendingUp,
    ArrowRight, Phone
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { useAuth } from '@/context/auth-context'
import { mockAbroadLeads, mockPartnerLeads } from '@/data/admin-mock-data'

export function CounsellorHomeView() {
    const { user } = useAuth()

    // Get leads assigned to this user
    const myAbroadLeads = mockAbroadLeads.filter(l => l.assignedMentorId === user?.id)
    const myPartnerLeads = mockPartnerLeads.filter(l => l.assignedMentorId === user?.id)

    const allLeads = [
        ...myAbroadLeads.map(l => ({ ...l, type: 'Abroad', source: l.collegeName })),
        ...myPartnerLeads.map(l => ({ ...l, type: 'Partner', source: l.instituteName }))
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Calculate stats
    const totalLeads = allLeads.length
    const pendingLeads = allLeads.filter(l => ['new', 'contacted'].includes(l.status)).length
    const inProgressLeads = allLeads.filter(l => ['counselling', 'applied'].includes(l.status)).length
    const convertedLeads = allLeads.filter(l => ['admitted', 'enrolled'].includes(l.status)).length

    const conversionRate = totalLeads > 0 ? Math.round((convertedLeads / totalLeads) * 100) : 0

    // Get recent pending leads for "Today's Follow-ups" (simulated)
    const recentPending = allLeads
        .filter(l => ['new', 'contacted'].includes(l.status))
        .slice(0, 5)

    const stats = [
        { label: 'Total Leads', value: totalLeads, icon: Users, color: 'bg-blue-500', trend: '+12%' },
        { label: 'Pending Action', value: pendingLeads, icon: Clock, color: 'bg-amber-500', trend: '-5%' },
        { label: 'In Progress', value: inProgressLeads, icon: TrendingUp, color: 'bg-indigo-500', trend: '+8%' },
        { label: 'Conversion Rate', value: `${conversionRate}%`, icon: CheckCircle, color: 'bg-emerald-500', trend: '+2%' },
    ]

    return (
        <div className="space-y-8 pb-24 lg:pb-0">
            {/* Valid Greeting & Overview */}
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">
                    Good Morning, {user?.name?.split(' ')[0]}! 👋
                </h2>
                <p className="text-theme-secondary">Here's what's happening with your leads today.</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                    <GlassmorphicCard key={index} className="p-5 flex items-start justify-between">
                        <div>
                            <p className="text-sm font-medium text-theme-muted">{stat.label}</p>
                            <h3 className="text-2xl font-bold text-theme-primary mt-1">{stat.value}</h3>
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded mt-2 inline-block ${stat.trend.startsWith('+')
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                }`}>
                                {stat.trend} from last week
                            </span>
                        </div>
                        <div className={`p-3 rounded-xl text-white ${stat.color} shadow-lg shadow-blue-500/10`}>
                            <stat.icon className="h-5 w-5" />
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Recent Activity / Tasks */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-theme-primary">Priority Follow-ups</h3>
                        <Link to="/counsellor/dashboard?view=leads" className="text-sm text-primary-blue hover:underline flex items-center gap-1">
                            View All <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="space-y-3">
                        {recentPending.length > 0 ? (
                            recentPending.map((lead) => (
                                <GlassmorphicCard key={lead.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:border-primary-blue/30 transition-colors">
                                    <div className="flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${lead.type === 'Abroad' ? 'bg-indigo-500' : 'bg-orange-500'
                                            }`}>
                                            {lead.studentName.charAt(0)}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-theme-primary group-hover:text-primary-blue transition-colors">
                                                {lead.studentName}
                                            </h4>
                                            <div className="flex items-center gap-3 text-xs text-theme-secondary mt-1">
                                                <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {lead.phone}</span>
                                                <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                                                <span>{lead.source}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 pl-14 sm:pl-0">
                                        <div className={`px-2.5 py-1 rounded-md text-xs font-medium uppercase ${lead.status === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                            {lead.status}
                                        </div>
                                        <Link
                                            to={`/counsellor/dashboard?view=leads&edit=${lead.id}`}
                                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-theme-secondary transition-colors"
                                            title="View Details"
                                        >
                                            <ArrowRight className="h-4 w-4" />
                                        </Link>
                                    </div>
                                </GlassmorphicCard>
                            ))
                        ) : (
                            <div className="text-center py-10 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                                <p className="text-theme-secondary">No pending leads! Great job. 🎉</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar Widgets */}
                <div className="space-y-6">
                    {/* Quick Actions Removed as per user request */}

                    <GlassmorphicCard className="p-5 bg-gradient-to-br from-primary-blue to-indigo-600 text-white border-none">
                        <h3 className="font-bold text-lg mb-1">Weekly Goal</h3>
                        <p className="text-blue-100 text-sm mb-4">Convert 5 more leads to reach your weekly target.</p>

                        <div className="space-y-2">
                            <div className="flex justify-between text-xs font-medium">
                                <span>Progress</span>
                                <span>75%</span>
                            </div>
                            <div className="h-2 bg-black/20 rounded-full overflow-hidden">
                                <div className="h-full bg-white w-3/4 rounded-full" />
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            </div>
        </div>
    )
}
